import { StrictMode, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { Icon } from "./components/icons";
import {
  connectErrorMessage,
  connectProblemError,
  connectProblemMessage,
  isDemoMode,
  session,
} from "./lib/connect";
import {
  DemoCycleRepository,
  MdbaseCycleRepository,
} from "./lib/repository";
import type { ConnectionDetails } from "./lib/types";
import "./styles.css";

function Root() {
  if (isDemoMode) {
    return <App repository={new DemoCycleRepository()} />;
  }
  return <ConnectedRoot />;
}

function ConnectedRoot() {
  const snapshot = useSyncExternalStore(
    (listener) => session.subscribe(listener),
    () => session.getSnapshot(),
  );
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void session.start().then((outcome) => {
      if (active && !outcome.ok) {
        setError(connectProblemMessage(outcome.problem));
      }
    }).catch((reason: unknown) => {
      if (active) setError(connectErrorMessage(reason));
    });
    // The SDK session is the application-level state owner. Do not destroy the
    // global session during React StrictMode's development remount.
    return () => {
      active = false;
    };
  }, []);

  const activeConnection = session.connection();
  const repository = useMemo(
    () => activeConnection ? new MdbaseCycleRepository(activeConnection) : null,
    [activeConnection],
  );

  if (
    snapshot.status === "not_started" ||
    snapshot.status === "starting" ||
    snapshot.status === "checking_setup"
  ) {
    return <ConnectionLoading />;
  }

  if (snapshot.status === "start_failed") {
    return (
      <StartupFailure
        message={error || connectProblemMessage(snapshot.problem)}
        onRetry={() => {
          setError("");
          void session.start().then((outcome) => {
            if (!outcome.ok) setError(connectProblemMessage(outcome.problem));
          }).catch((reason: unknown) => setError(connectErrorMessage(reason)));
        }}
      />
    );
  }

  if (snapshot.status === "destroyed") {
    return <StartupFailure message="This Tide session has ended. Reload the page to reconnect." />;
  }

  if (snapshot.status === "setup_review_required") {
    return <SetupGate snapshot={snapshot} error={error} onError={setError} />;
  }

  if (snapshot.status !== "ready") {
    return <ConnectGate snapshot={snapshot} error={error} onError={setError} />;
  }
  if (!repository || !activeConnection) {
    return <StartupFailure message="mdbase Connect did not provide the selected collection connection." />;
  }

  const connection: ConnectionDetails = {
    info: snapshot.info,
    async checkDirectAccess() {
      const outcome = await activeConnection.checkDirectAccess();
      if (!outcome.ok) throw connectProblemError(outcome.problem);
    },
    async requestDirectAccess() {
      const outcome = await activeConnection.requestDirectAccess();
      if (!outcome.ok) throw connectProblemError(outcome.problem);
    },
    switchCollection() {
      const outcome = session.clearSelection({ history: "replace" });
      if (!outcome.ok) setError(connectProblemMessage(outcome.problem));
    },
    forgetCollection() {
      const outcome = session.forget(snapshot.collectionId);
      if (!outcome.ok) setError(connectProblemMessage(outcome.problem));
    },
  };
  return <App repository={repository} connection={connection} />;
}

function SetupGate({
  snapshot,
  error,
  onError,
}: {
  snapshot: Extract<ReturnType<typeof session.getSnapshot>, { status: "setup_review_required" }>;
  error: string;
  onError(message: string): void;
}) {
  const [applying, setApplying] = useState(false);

  function chooseAnotherCollection() {
    const outcome = session.clearSelection();
    if (!outcome.ok) onError(connectProblemMessage(outcome.problem));
  }

  async function applySetup() {
    setApplying(true);
    onError("");
    try {
      const outcome = await session.applyCollectionSetup();
      if (!outcome.ok) onError(connectProblemMessage(outcome.problem));
    } catch (reason) {
      onError(connectErrorMessage(reason));
    } finally {
      setApplying(false);
    }
  }

  return (
    <main className="connect-page">
      <div className="connect-brand">
        <span className="brand-mark"><Icon name="wave" /></span>
        <strong>Tide</strong>
      </div>
      <section className="connect-card">
        <div className="connect-visual" aria-hidden="true">
          <span className="orbit orbit-one" />
          <span className="orbit orbit-two" />
          <span className="connect-moon"><Icon name="moon" /></span>
          <span className="connect-drop"><Icon name="drop" /></span>
        </div>
        <div className="connect-copy">
          <p className="eyebrow">Collection setup</p>
          <h1>Prepare {snapshot.info.displayName} for Tide.</h1>
          <p>{snapshot.update.reason}</p>
          <ul>
            {snapshot.update.typePacks.map((pack) => (
              <li key={pack.id}><Icon name="check" /> {pack.name}: {pack.reason}</li>
            ))}
          </ul>
          {error && <p className="connect-error" role="alert">{error}</p>}
          <div className="connect-actions">
            <button
              type="button"
              className="primary"
              disabled={applying || !snapshot.update.canApply}
              onClick={() => void applySetup()}
            >
              {applying ? "Updating collection…" : "Apply reviewed setup"}
            </button>
            <button type="button" className="secondary" disabled={applying} onClick={chooseAnotherCollection}>
              Choose another collection
            </button>
          </div>
          <small className="connect-footnote">
            Tide applies only the exact contract and cycle-day type changes reviewed by mdbase Connect.
          </small>
        </div>
      </section>
    </main>
  );
}

function ConnectGate({
  snapshot,
  error,
  onError,
}: {
  snapshot: Exclude<ReturnType<typeof session.getSnapshot>, { status: "ready" | "not_started" | "starting" | "start_failed" | "destroyed" | "checking_setup" | "setup_review_required" }>;
  error: string;
  onError(message: string): void;
}) {
  const [connecting, setConnecting] = useState(false);
  const requiresAuthorization = snapshot.status === "authorization_required" || snapshot.status === "unavailable";
  const snapshotProblem = snapshot.status === "blocked" ? snapshot.problem.message : "";

  async function authorize(target: "choose" | "selected") {
    setConnecting(true);
    onError("");
    try {
      const outcome = await session.authorize(target);
      if (!outcome.ok) onError(connectProblemMessage(outcome.problem));
    } catch (reason) {
      onError(connectErrorMessage(reason));
    } finally {
      setConnecting(false);
    }
  }

  function select(collectionId: string) {
    const outcome = session.select(collectionId, { history: "replace" });
    if (!outcome.ok) onError(connectProblemMessage(outcome.problem));
  }

  return (
    <main className="connect-page">
      <div className="connect-brand">
        <span className="brand-mark"><Icon name="wave" /></span>
        <strong>Tide</strong>
      </div>
      <section className="connect-card">
        <div className="connect-visual" aria-hidden="true">
          <span className="orbit orbit-one" />
          <span className="orbit orbit-two" />
          <span className="connect-moon"><Icon name="moon" /></span>
          <span className="connect-drop"><Icon name="drop" /></span>
        </div>
        <div className="connect-copy">
          <p className="eyebrow">Private cycle notes</p>
          <h1>Your rhythm belongs to you.</h1>
          <p>
            Choose an mdbase collection for period, symptom and mood notes. mdbase Connect reviews Tide's setup and required record capabilities before granting access.
          </p>
          <ul>
            <li><Icon name="check" /> Plain Markdown files you can keep</li>
            <li><Icon name="check" /> No advertising profile or app database</li>
            <li><Icon name="check" /> Revoke access whenever you want</li>
          </ul>
          {(error || snapshotProblem) && <p className="connect-error" role="alert">{error || snapshotProblem}</p>}
          {requiresAuthorization ? (
            <div className="connect-actions">
              <button type="button" className="primary" disabled={connecting} onClick={() => void authorize("selected")}>
                {connecting ? "Opening mdbase Connect…" : "Authorize this collection again"}
              </button>
              <button type="button" className="secondary" disabled={connecting} onClick={() => void authorize("choose")}>
                Choose another collection
              </button>
            </div>
          ) : (
            <>
              {snapshot.connections.length > 0 && (
                <div className="saved-connections">
                  {snapshot.connections.map((connection) => (
                    <button type="button" key={connection.collectionId} onClick={() => select(connection.collectionId)}>
                      <span className="collection-mark"><Icon name="note" /></span>
                      <span><strong>{connection.displayName}</strong><small>Previously authorized</small></span>
                      <Icon name="chevron-right" />
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="primary wide" disabled={connecting} onClick={() => void authorize("choose")}>
                {connecting ? "Opening mdbase Connect…" : snapshot.connections.length ? "Choose another collection" : "Choose your collection"}
              </button>
            </>
          )}
          <small className="connect-footnote">
            Predictions are estimates and are not intended for contraception or medical diagnosis.
          </small>
        </div>
      </section>
    </main>
  );
}

function ConnectionLoading() {
  return (
    <main className="connect-page loading">
      <div className="connect-brand">
        <span className="brand-mark"><Icon name="wave" /></span>
        <strong>Tide</strong>
      </div>
      <div className="connection-loading">
        <span className="loading-mark"><Icon name="wave" /></span>
        <p>Checking for your collection…</p>
      </div>
    </main>
  );
}

function StartupFailure({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <main className="state-page">
      <span className="error-mark">!</span>
      <h1>Tide could not start mdbase Connect.</h1>
      <p>{message}</p>
      {onRetry && <button type="button" className="primary" onClick={onRetry}>Try again</button>}
    </main>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
